/**
 * Turning a spot on the week grid into a bookable slot.
 *
 * The grid is drawn in wall-clock minutes; the API answers with UTC instants
 * on whatever granularity the therapist's session length implies. Matching the
 * two here keeps that arithmetic out of the modal and lets it be tested with
 * awkward durations (45 minutes, say) rather than only round hours.
 */

import { formatSlotTime } from "./formatting";
import { TimeSlot } from "./types";
import { minutesOfDayTime } from "./weekLayout";

/** Where a slot starts, as minutes since midnight in `timeZone`. */
export function slotStartMinute(slot: TimeSlot, timeZone: string): number {
    return minutesOfDayTime(formatSlotTime(slot.start, timeZone));
}

/**
 * The slot a click at `minute` means, or null when it means none.
 *
 * A click lands on the slot starting exactly there; failing that, on the one
 * that covers it — slots start every quarter hour, so several overlap and the
 * exact start is the one meant; failing that, on one starting within the next
 * `windowMinutes` — clicking the 09:00 row with slots at 09:15
 * plainly means that one. Nothing further out is ever chosen: silently
 * selecting 14:00 because the morning is full would commit a time the
 * therapist never pointed at.
 */
export function slotMatchingMinute(
    slots: readonly TimeSlot[],
    timeZone: string,
    minute: number,
    windowMinutes = 60
): string | null {
    const starts = slots.map((slot) => ({
        start: slot.start,
        startMinute: slotStartMinute(slot, timeZone),
        duration: slot.duration_minutes,
    }));

    const exact = starts.find((slot) => slot.startMinute === minute);
    if (exact) return exact.start;

    const covering = starts.find(
        (slot) =>
            slot.startMinute <= minute &&
            minute < slot.startMinute + slot.duration
    );
    if (covering) return covering.start;

    const withinReach = starts
        .filter(
            (slot) =>
                slot.startMinute >= minute &&
                slot.startMinute < minute + windowMinutes
        )
        .sort((a, b) => a.startMinute - b.startMinute);

    return withinReach[0]?.start ?? null;
}

/**
 * Minutes since midnight for what someone typed into a time field, or null.
 *
 * Separators are optional: "9", "09", "930", "9:30", "0930" and "13.15" all
 * read the obvious way. One or two digits are an hour on the hour.
 */
export function parseTypedTime(text: string): number | null {
    const digits = text.replace(/\D/g, "");
    if (digits.length === 0 || digits.length > 4) return null;
    const hours = Number(digits.length <= 2 ? digits : digits.slice(0, -2));
    const minutes = digits.length <= 2 ? 0 : Number(digits.slice(-2));
    if (hours > 23 || minutes > 59) return null;
    return hours * 60 + minutes;
}

/**
 * What a typed time means among the free starts, as indexes into `slots`.
 *
 * `exact` is the slot starting at precisely that time — the only one a typed
 * time may book. `nearest` is the first start at or after it (the last one
 * when the day ends earlier), for highlighting while the typing is underway.
 */
export function slotMatchingTyped(
    slots: readonly TimeSlot[],
    timeZone: string,
    text: string
): { exact: number | null; nearest: number | null } {
    const minute = parseTypedTime(text);
    if (minute === null || slots.length === 0) return { exact: null, nearest: null };

    const starts = slots.map((slot) => slotStartMinute(slot, timeZone));
    const exact = starts.indexOf(minute);
    const after = starts.findIndex((start) => start >= minute);
    return {
        exact: exact >= 0 ? exact : null,
        nearest: after >= 0 ? after : starts.length - 1,
    };
}
